/**
 * FACTURA FORM DIALOG - Crear factura con selector multi-orden
 *
 * Soporta tres modos:
 * - Modo Orden:    busca por número de orden → auto-rellena la factura (DEFAULT)
 * - Modo Paciente: busca paciente → carga órdenes del paciente
 * - Modo Cliente:  busca cliente  → carga órdenes del cliente (multi-paciente)
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Search, X, User, Building2, Hash, Receipt, ClipboardList } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import { useDebounce } from "@/hooks/use-debounce";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreateFactura,
  useOrdenesDisponibles,
  useOrdenesDisponiblesPorCliente,
  useOrdenPorNumero,
  type OrdenDisponible,
} from "@/hooks/use-facturacion";
import { useBuscarPacientes, type PacienteListItem } from "@/hooks/use-pacientes";
import { useBuscarClientes, type ClienteListItem } from "@/hooks/use-clientes";
import { useSucursal } from "@/contexts/sucursal-context";
import {
  calcularTotalOrdenes,
  calcularDescuentoPagosDirectos,
} from "@/pages/facturacion/utils/factura-calculations";
import {
  agruparPorPaciente,
  resetFormState,
  autoFillCliente,
  autoFillOrden,
  type ModoFactura,
} from "@/pages/facturacion/utils/factura-mode";

// =============================================================================
// Schema
// =============================================================================

const facturaSchema = z.object({
  nombre_cliente: z.string().optional(),
  rtn_cliente: z.string().optional(),
  descuento_global: z.string().optional(),
  observaciones: z.string().optional(),
});

type FormData = z.infer<typeof facturaSchema>;

// =============================================================================
// Props
// =============================================================================

interface FacturaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (factura: import("@/services/facturacion").Factura) => void;
}

// =============================================================================
// TabOrdenDirecta — sub-componente para buscar por número de orden
// =============================================================================

interface TabOrdenDirectaProps {
  sucursalId: number | null;
  selectedOrden: OrdenDisponible | null;
  onSelectOrden: (orden: OrdenDisponible) => void;
  onClear: () => void;
}

function TabOrdenDirecta({
  sucursalId,
  selectedOrden,
  onSelectOrden,
  onClear,
}: TabOrdenDirectaProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const { data: resultados = [], isLoading } = useOrdenPorNumero(debouncedSearch, sucursalId, {
    enabled: !selectedOrden,
  });

  // Auto-selección cuando hay exactamente un resultado
  useEffect(() => {
    if (!selectedOrden && resultados.length === 1 && debouncedSearch.length >= 3) {
      onSelectOrden(resultados[0]);
    }
  }, [resultados, debouncedSearch, selectedOrden, onSelectOrden]);

  if (selectedOrden) {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-md border p-3 bg-muted/30">
          <Hash className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-mono font-semibold">{selectedOrden.numero}</p>
            {selectedOrden.paciente_nombre && (
              <p className="text-xs text-muted-foreground">{selectedOrden.paciente_nombre}</p>
            )}
            <p className="text-xs text-muted-foreground">{selectedOrden.fecha}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={onClear}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        {/* Resumen de exámenes de la orden seleccionada */}
        <div className="rounded-md border bg-muted/10 p-2.5 space-y-1">
          {selectedOrden.examenes.map((ex) => (
            <div key={ex.id} className="flex justify-between text-xs">
              <span>{ex.nombre}</span>
              <span>L. {parseFloat(ex.precio).toFixed(2)}</span>
            </div>
          ))}
          <Separator className="my-1" />
          <div className="flex justify-between text-xs font-semibold">
            <span>{t("facturacion.multiOrden.totalGeneral")}</span>
            <span>L. {parseFloat(selectedOrden.total).toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("facturacion.ordenDirecta.placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          autoComplete="off"
          autoFocus
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {debouncedSearch.length < 3 && (
        <p className="text-xs text-muted-foreground">
          {t("facturacion.ordenDirecta.ingresaNumero")}
        </p>
      )}

      {debouncedSearch.length >= 3 && !isLoading && resultados.length === 0 && (
        <p className="text-sm text-muted-foreground py-1">
          {t("facturacion.ordenDirecta.sinResultados")}
        </p>
      )}

      {resultados.length > 1 && (
        <div className="rounded-md border divide-y max-h-[200px] overflow-y-auto">
          {resultados.map((orden) => (
            <button
              key={orden.id}
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-accent text-sm"
              onClick={() => onSelectOrden(orden)}
            >
              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-mono font-medium">{orden.numero}</span>
                {orden.paciente_nombre && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    {orden.paciente_nombre}
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground shrink-0">
                L. {parseFloat(orden.total).toFixed(2)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// FacturaFormDialog — componente principal
// =============================================================================

export function FacturaFormDialog({ open, onOpenChange, onSuccess }: FacturaFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva, isAllSucursalesSelected } = useSucursal();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [modo, setModo] = useState<ModoFactura>("orden");
  // "paciente" = solo ID del paciente, "cliente" = nombre+RTN, "ambos" = los dos
  type ModoFacturacion = "paciente" | "cliente" | "ambos";
  const [modoFacturacion, setModoFacturacion] = useState<ModoFacturacion>("paciente");

  // ── Modo Orden ─────────────────────────────────────────────────────────────
  const [selectedOrden, setSelectedOrden] = useState<OrdenDisponible | null>(null);

  // ── Modo Paciente ──────────────────────────────────────────────────────────
  const [pacienteSearch, setPacienteSearch] = useState("");
  const [selectedPaciente, setSelectedPaciente] = useState<PacienteListItem | null>(null);

  const { data: pacienteResults = [], isFetching: isSearchingPaciente } = useBuscarPacientes(
    pacienteSearch,
    { enabled: !selectedPaciente && pacienteSearch.length >= 2 }
  );

  // ── Modo Cliente ───────────────────────────────────────────────────────────
  const [clienteSearch, setClienteSearch] = useState("");
  const [selectedCliente, setSelectedCliente] = useState<ClienteListItem | null>(null);

  const { data: clienteResults = [], isFetching: isSearchingCliente } = useBuscarClientes(
    clienteSearch,
    { enabled: !selectedCliente && clienteSearch.length >= 2 }
  );

  // ── Órdenes seleccionadas (modos paciente/cliente) ─────────────────────────
  const [selectedOrdenIds, setSelectedOrdenIds] = useState<Set<number>>(new Set());

  const sucursalId = isAllSucursalesSelected ? null : (sucursalActiva?.id ?? null);

  // ── Queries de órdenes (modos paciente/cliente) ────────────────────────────
  const { data: ordenesPaciente = [], isLoading: isLoadingPaciente } = useOrdenesDisponibles(
    selectedPaciente?.id ?? null,
    sucursalId,
    { enabled: modo === "paciente" && selectedPaciente != null && sucursalId != null }
  );

  const { data: ordenesCliente = [], isLoading: isLoadingCliente } =
    useOrdenesDisponiblesPorCliente(selectedCliente?.id ?? null, sucursalId, {
      enabled: modo === "cliente" && selectedCliente != null && sucursalId != null,
    });

  const ordenesDisponibles = modo === "paciente" ? ordenesPaciente : ordenesCliente;
  const isLoadingOrdenes = modo === "paciente" ? isLoadingPaciente : isLoadingCliente;

  const createMutation = useCreateFactura({
    onSuccess: (factura) => onSuccess(factura),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending;

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormData>({
    resolver: zodResolver(facturaSchema),
    defaultValues: {
      nombre_cliente: "",
      rtn_cliente: "",
      descuento_global: "0",
      observaciones: "",
    },
  });

  const nombreClienteValue = watch("nombre_cliente");

  // Indica si los campos de empresa fueron auto-rellenados desde la orden
  const autoFilledFromOrden =
    modoFacturacion === "cliente" &&
    !!nombreClienteValue &&
    (modo === "orden"
      ? selectedOrden?.cliente_id != null
      : ordenesDisponibles.some((o) => selectedOrdenIds.has(o.id) && o.cliente_id != null));

  // ── Reset completo al abrir/cerrar ─────────────────────────────────────────
  useEffect(() => {
    if (open) {
      reset({ nombre_cliente: "", rtn_cliente: "", descuento_global: "0", observaciones: "" });
      setError(null);
      setModo("orden");
      setModoFacturacion("paciente");
      setSelectedOrden(null);
      setPacienteSearch("");
      setSelectedPaciente(null);
      setClienteSearch("");
      setSelectedCliente(null);
      setSelectedOrdenIds(new Set());
    }
  }, [open, reset]);

  // ── Cambio de modo ─────────────────────────────────────────────────────────
  const handleModoChange = useCallback(
    (value: string) => {
      if (!value || value === modo) return;
      // Limpiar cache de búsqueda por número al salir del modo orden
      if (modo === "orden") {
        queryClient.removeQueries({ queryKey: queryKeys.facturas.all });
      }
      const state = resetFormState();
      setModo(value as ModoFactura);
      setSelectedOrden(null);
      setSelectedPaciente(null);
      setPacienteSearch("");
      setSelectedCliente(null);
      setClienteSearch("");
      setSelectedOrdenIds(new Set());
      setValue("nombre_cliente", state.nombreCliente);
      setValue("rtn_cliente", state.rtnCliente);
      setModoFacturacion("paciente");
      setError(null);
    },
    [modo, setValue, queryClient]
  );

  // ── Selección de orden (modo orden) ───────────────────────────────────────
  const handleSelectOrden = useCallback(
    (orden: OrdenDisponible) => {
      const filled = autoFillOrden(orden, resetFormState());
      setSelectedOrden(orden);
      setSelectedOrdenIds(new Set(filled.selectedIds));
      // Si la orden tiene cliente asociado → auto-seleccionar modo "cliente" y pre-llenar
      if (orden.cliente_id != null) {
        setModoFacturacion("cliente");
        setValue("nombre_cliente", filled.nombreCliente);
        setValue("rtn_cliente", filled.rtnCliente);
      } else {
        // Sin cliente → resetear a "paciente" y limpiar campos
        setModoFacturacion("paciente");
        setValue("nombre_cliente", "");
        setValue("rtn_cliente", "");
      }
    },
    [setValue]
  );

  const handleClearOrden = useCallback(() => {
    const state = resetFormState();
    setSelectedOrden(null);
    setSelectedOrdenIds(new Set());
    setModoFacturacion("paciente");
    setValue("nombre_cliente", state.nombreCliente);
    setValue("rtn_cliente", state.rtnCliente);
  }, [setValue]);

  // ── Auto-completar cliente ─────────────────────────────────────────────────
  useEffect(() => {
    if (modo === "cliente" && selectedCliente && !nombreClienteValue) {
      const filled = autoFillCliente(
        { nombre: selectedCliente.nombre, rtn: selectedCliente.rtn },
        { selectedIds: [], nombreCliente: "", rtnCliente: watch("rtn_cliente") ?? "" }
      );
      setValue("nombre_cliente", filled.nombreCliente);
      setValue("rtn_cliente", filled.rtnCliente);
    }
  }, [selectedCliente, modo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Órdenes seleccionadas para resumen ─────────────────────────────────────
  const ordenesSeleccionadas = useMemo(() => {
    if (modo === "orden") return selectedOrden ? [selectedOrden] : [];
    return ordenesDisponibles.filter((o) => selectedOrdenIds.has(o.id));
  }, [modo, selectedOrden, ordenesDisponibles, selectedOrdenIds]);

  const totalCalculado = useMemo(
    () => calcularTotalOrdenes(ordenesSeleccionadas),
    [ordenesSeleccionadas]
  );
  const descuentoPagosDirectos = useMemo(
    () => calcularDescuentoPagosDirectos(ordenesSeleccionadas),
    [ordenesSeleccionadas]
  );

  // ── Handlers paciente/cliente ──────────────────────────────────────────────
  const handleSelectPaciente = useCallback((p: PacienteListItem) => {
    setSelectedPaciente(p);
    setPacienteSearch("");
    setSelectedOrdenIds(new Set());
  }, []);

  const handleClearPaciente = useCallback(() => {
    setSelectedPaciente(null);
    setPacienteSearch("");
    setSelectedOrdenIds(new Set());
  }, []);

  const handleSelectCliente = useCallback(
    (c: ClienteListItem) => {
      setSelectedCliente(c);
      setClienteSearch("");
      setSelectedOrdenIds(new Set());
      const filled = autoFillCliente(
        { nombre: c.nombre, rtn: c.rtn },
        { selectedIds: [], nombreCliente: "", rtnCliente: "" }
      );
      setValue("nombre_cliente", filled.nombreCliente);
      setValue("rtn_cliente", filled.rtnCliente);
    },
    [setValue]
  );

  const handleClearCliente = useCallback(() => {
    const state = resetFormState();
    setSelectedCliente(null);
    setClienteSearch("");
    setSelectedOrdenIds(new Set());
    setValue("nombre_cliente", state.nombreCliente);
    setValue("rtn_cliente", state.rtnCliente);
  }, [setValue]);

  const handleToggleOrden = useCallback((ordenId: number) => {
    setSelectedOrdenIds((prev) => {
      const next = new Set(prev);
      if (next.has(ordenId)) next.delete(ordenId);
      else next.add(ordenId);
      return next;
    });
  }, []);

  // ── Auto-completar cliente en modo paciente cuando se seleccionan órdenes ──
  // Si la primera orden seleccionada tiene cliente → auto-seleccionar modo "cliente"
  useEffect(() => {
    if (modo !== "paciente" || selectedOrdenIds.size === 0) return;

    // Buscar la primera orden seleccionada que tenga cliente
    const primeraConCliente = ordenesDisponibles.find(
      (o) => selectedOrdenIds.has(o.id) && o.cliente_id != null
    );

    if (primeraConCliente && modoFacturacion === "paciente") {
      setModoFacturacion("cliente");
      setValue("nombre_cliente", primeraConCliente.cliente_nombre ?? "");
      setValue("rtn_cliente", primeraConCliente.cliente_rtn ?? "");
    }
  }, [selectedOrdenIds, ordenesDisponibles, modo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Submit ─────────────────────────────────────────────────────────────────
  const onSubmit = (data: FormData) => {
    const ids =
      modo === "orden" ? (selectedOrden ? [selectedOrden.id] : []) : Array.from(selectedOrdenIds);

    if (ids.length === 0) {
      setError(t("facturacion.errors.empty_order_list"));
      return;
    }
    setError(null);

    // Resolver nombre_cliente y rtn_cliente según modoFacturacion
    // En modo "cliente" empresarial siempre se usan los campos del form
    let nombreCliente: string | undefined;
    let rtnCliente: string | undefined;

    if (modo === "cliente") {
      // Modo empresarial: siempre nombre + RTN del cliente
      nombreCliente = data.nombre_cliente || undefined;
      rtnCliente = data.rtn_cliente || undefined;
    } else {
      // Modos orden/paciente: depende de modoFacturacion
      if (modoFacturacion === "paciente") {
        // Solo paciente — no enviar nombre_cliente ni rtn_cliente
        nombreCliente = undefined;
        rtnCliente = undefined;
      } else if (modoFacturacion === "cliente") {
        // Solo cliente/empresa — nombre + RTN del form
        nombreCliente = data.nombre_cliente || undefined;
        rtnCliente = data.rtn_cliente || undefined;
      } else {
        // Ambos — nombre + RTN del form (el paciente ya está en la orden)
        nombreCliente = data.nombre_cliente || undefined;
        rtnCliente = data.rtn_cliente || undefined;
      }
    }

    // Resolver modo_facturacion para el backend
    let modoFacturacionBackend: "PACIENTE" | "EMPRESA" | "AMBOS";
    if (modo === "cliente") {
      modoFacturacionBackend = "EMPRESA";
    } else {
      const map = { paciente: "PACIENTE", cliente: "EMPRESA", ambos: "AMBOS" } as const;
      modoFacturacionBackend = map[modoFacturacion];
    }

    createMutation.mutate({
      orden_ids: ids,
      nombre_cliente: nombreCliente,
      rtn_cliente: rtnCliente,
      descuento_global: parseFloat(data.descuento_global ?? "0") || 0,
      observaciones: data.observaciones || undefined,
      modo_facturacion: modoFacturacionBackend,
    });
  };

  // ── Grupos para modo cliente ───────────────────────────────────────────────
  const gruposCliente = useMemo(
    () => (modo === "cliente" ? agruparPorPaciente(ordenesDisponibles) : []),
    [modo, ordenesDisponibles]
  );

  const hasSelection = modo === "orden" ? selectedOrden != null : selectedOrdenIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[580px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("facturacion.form.title")}</DialogTitle>
          <DialogDescription>{t("facturacion.multiOrden.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {/* ── Toggle Orden / Paciente / Cliente ── */}
          <div className="space-y-1.5">
            <Label>{t("common.type", "Tipo")}</Label>
            <ToggleGroup
              type="single"
              value={modo}
              onValueChange={handleModoChange}
              className="justify-start"
            >
              <ToggleGroupItem value="orden" aria-label={t("facturacion.modoCliente.toggle.orden")}>
                <Hash className="h-4 w-4 mr-1.5" />
                {t("facturacion.modoCliente.toggle.orden")}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="paciente"
                aria-label={t("facturacion.modoCliente.toggle.paciente")}
              >
                <User className="h-4 w-4 mr-1.5" />
                {t("facturacion.modoCliente.toggle.paciente")}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="cliente"
                aria-label={t("facturacion.modoCliente.toggle.cliente")}
              >
                <Building2 className="h-4 w-4 mr-1.5" />
                {t("facturacion.modoCliente.toggle.cliente")}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* ── Tab: Desde Orden ── */}
          {modo === "orden" && (
            <div className="space-y-2">
              <Label>{t("facturacion.ordenDirecta.titulo")}</Label>
              {isAllSucursalesSelected ? (
                <p className="text-xs text-amber-600">
                  {t("facturacion.modoCliente.seleccionarSucursal")}
                </p>
              ) : (
                <TabOrdenDirecta
                  sucursalId={sucursalId}
                  selectedOrden={selectedOrden}
                  onSelectOrden={handleSelectOrden}
                  onClear={handleClearOrden}
                />
              )}
            </div>
          )}

          {/* ── Tab: Paciente ── */}
          {modo === "paciente" && (
            <div className="space-y-2">
              <Label>{t("facturacion.multiOrden.paciente")} *</Label>
              {selectedPaciente ? (
                <div className="flex items-center gap-2 rounded-md border p-2.5 bg-muted/30">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{selectedPaciente.nombre_completo}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedPaciente.identificacion}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={handleClearPaciente}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t("facturacion.multiOrden.searchPaciente")}
                    value={pacienteSearch}
                    onChange={(e) => setPacienteSearch(e.target.value)}
                    className="pl-9"
                    autoComplete="off"
                  />
                  {isSearchingPaciente && (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                  {pacienteResults.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-[180px] overflow-y-auto">
                      {pacienteResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent text-sm"
                          onClick={() => handleSelectPaciente(p)}
                        >
                          <span className="font-medium">{p.nombre_completo}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            {p.identificacion}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Cliente ── */}
          {modo === "cliente" && (
            <div className="space-y-2">
              <Label>{t("facturacion.modoCliente.toggle.cliente")} *</Label>
              {isAllSucursalesSelected && (
                <p className="text-xs text-amber-600">
                  {t("facturacion.modoCliente.seleccionarSucursal")}
                </p>
              )}
              {selectedCliente ? (
                <div className="flex items-center gap-2 rounded-md border p-2.5 bg-muted/30">
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{selectedCliente.nombre}</p>
                    {selectedCliente.rtn && (
                      <p className="text-xs text-muted-foreground">RTN: {selectedCliente.rtn}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={handleClearCliente}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={t("facturacion.modoCliente.buscarCliente")}
                    value={clienteSearch}
                    onChange={(e) => setClienteSearch(e.target.value)}
                    className="pl-9"
                    autoComplete="off"
                    disabled={isAllSucursalesSelected}
                  />
                  {isSearchingCliente && (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                  {clienteResults.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-[180px] overflow-y-auto">
                      {clienteResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent text-sm"
                          onClick={() => handleSelectCliente(c)}
                        >
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{c.nombre}</span>
                            {c.rtn && (
                              <span className="text-muted-foreground ml-2 text-xs">{c.rtn}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Órdenes disponibles (modos paciente/cliente) ── */}
          {(modo === "paciente" && selectedPaciente) || (modo === "cliente" && selectedCliente) ? (
            <div className="space-y-2">
              <Label>{t("facturacion.multiOrden.ordenesDisponibles")}</Label>
              {isLoadingOrdenes ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t("common.loading")}</span>
                </div>
              ) : ordenesDisponibles.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  {modo === "cliente"
                    ? t("facturacion.modoCliente.sinOrdenesDisponibles")
                    : t("facturacion.multiOrden.noOrdenesDisponibles")}
                </p>
              ) : modo === "cliente" ? (
                <OrdenesAgrupadasPorPaciente
                  grupos={gruposCliente}
                  selectedIds={selectedOrdenIds}
                  onToggle={handleToggleOrden}
                />
              ) : (
                <div className="rounded-md border divide-y max-h-[220px] overflow-y-auto">
                  {ordenesDisponibles.map((orden) => (
                    <OrdenRow
                      key={orden.id}
                      orden={orden}
                      checked={selectedOrdenIds.has(orden.id)}
                      onToggle={handleToggleOrden}
                    />
                  ))}
                </div>
              )}
              {selectedOrdenIds.size > 0 && (
                <p className="text-xs text-muted-foreground">
                  {t("facturacion.multiOrden.ordenesSeleccionadas", {
                    count: selectedOrdenIds.size,
                  })}
                </p>
              )}
            </div>
          ) : null}

          {/* ── Resumen de exámenes (modos paciente/cliente con selección) ── */}
          {modo !== "orden" && ordenesSeleccionadas.length > 0 && (
            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
              <p className="text-sm font-medium">{t("facturacion.multiOrden.resumenExamenes")}</p>
              {ordenesSeleccionadas.map((orden) => (
                <div key={orden.id} className="space-y-1">
                  <p className="text-xs font-mono text-muted-foreground">
                    {orden.numero}
                    {orden.paciente_nombre && (
                      <span className="ml-2 text-foreground/60">— {orden.paciente_nombre}</span>
                    )}
                  </p>
                  {orden.examenes.map((ex) => (
                    <div key={ex.id} className="flex justify-between text-xs pl-2">
                      <span>{ex.nombre}</span>
                      <span>L. {parseFloat(ex.precio).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
              <Separator />
              <div className="flex justify-between text-sm font-semibold">
                <span>{t("facturacion.multiOrden.totalGeneral")}</span>
                <span>L. {totalCalculado.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* ── Info descuento por pagos directos ── */}
          {descuentoPagosDirectos > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-green-50 dark:bg-green-950/30 p-2.5 text-xs text-green-700 dark:text-green-300">
              <span>
                {t("facturacion.form.descuentoPagosDirectosInfo", {
                  monto: descuentoPagosDirectos.toFixed(2),
                })}
              </span>
            </div>
          )}

          {/* ── Info auto-completar (modo cliente) ── */}
          {modo === "cliente" && ordenesSeleccionadas.length > 0 && (
            <div className="flex items-start gap-2 rounded-md bg-blue-50 dark:bg-blue-950/30 p-2.5 text-xs text-blue-700 dark:text-blue-300">
              <span>{t("facturacion.multiOrden.autoClienteInfo")}</span>
            </div>
          )}

          {/* ── Facturar a (modos orden y paciente) ── */}
          {(modo === "orden" || modo === "paciente") && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t("facturacion.form.facturarA", "Facturar a")}</Label>
                {autoFilledFromOrden && (
                  <span className="text-[10px] text-blue-600 dark:text-blue-400 flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {t("facturacion.form.autoRellenadoOrden", "Auto-completado desde la orden")}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    value: "paciente" as const,
                    icon: <User className="h-4 w-4" />,
                    label: t("facturacion.form.facturarPaciente", "Paciente"),
                    desc: t("facturacion.form.facturarPacienteDesc", "Nombre e ID"),
                  },
                  {
                    value: "cliente" as const,
                    icon: <Building2 className="h-4 w-4" />,
                    label: t("facturacion.form.facturarCliente", "Empresa"),
                    desc: t("facturacion.form.facturarClienteDesc", "Nombre y RTN"),
                  },
                  {
                    value: "ambos" as const,
                    icon: <Receipt className="h-4 w-4" />,
                    label: t("facturacion.form.facturarAmbos", "Ambos"),
                    desc: t("facturacion.form.facturarAmbosDesc", "Paciente + empresa"),
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      // Al cambiar a "paciente" limpiar campos empresa
                      // Al cambiar entre "cliente"/"ambos" preservar los valores ya escritos
                      if (opt.value === "paciente") {
                        setValue("nombre_cliente", "");
                        setValue("rtn_cliente", "");
                      }
                      setModoFacturacion(opt.value);
                    }}
                    className={`flex flex-col items-center gap-1 rounded-md border p-3 text-center transition-colors hover:bg-accent ${
                      modoFacturacion === opt.value
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {opt.icon}
                    <span className="text-xs font-medium leading-tight">{opt.label}</span>
                    <span className="text-[10px] leading-tight opacity-70">{opt.desc}</span>
                  </button>
                ))}
              </div>

              {/* Campos empresa — visibles en "cliente" y "ambos" */}
              {(modoFacturacion === "cliente" || modoFacturacion === "ambos") && (
                <div className="space-y-2 rounded-md border p-3 bg-muted/20">
                  <div className="space-y-1.5">
                    <Label htmlFor="nombre_cliente">
                      {t("facturacion.form.clienteOpcional", "Cliente (opcional)")}
                    </Label>
                    <Input
                      id="nombre_cliente"
                      placeholder={t(
                        "facturacion.form.clienteOpcionalPlaceholder",
                        "Nombre del cliente si difiere del paciente"
                      )}
                      {...register("nombre_cliente")}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="rtn_cliente">
                      RTN{" "}
                      <span className="text-muted-foreground text-xs">
                        {t("common.optional", "(opcional)")}
                      </span>
                    </Label>
                    <Input
                      id="rtn_cliente"
                      placeholder="0000-0000-000000"
                      {...register("rtn_cliente")}
                      autoComplete="off"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Nombre + RTN (modo cliente empresarial) ── */}
          {modo === "cliente" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="nombre_cliente">{t("facturacion.form.nombreCliente")}</Label>
                <Input
                  id="nombre_cliente"
                  placeholder={t("facturacion.form.nombreClientePlaceholder")}
                  {...register("nombre_cliente")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rtn_cliente">{t("facturacion.form.rtnCliente")}</Label>
                <Input
                  id="rtn_cliente"
                  placeholder="0000-0000-000000"
                  {...register("rtn_cliente")}
                />
              </div>
            </div>
          )}

          {/* ── Descuento ── */}
          <div className="space-y-2">
            <Label htmlFor="descuento_global">{t("facturacion.form.descuento")}</Label>
            <Input
              id="descuento_global"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              {...register("descuento_global")}
            />
          </div>

          {/* ── Observaciones ── */}
          <div className="space-y-2">
            <Label htmlFor="observaciones">{t("facturacion.form.observaciones")}</Label>
            <Textarea
              id="observaciones"
              placeholder={t("facturacion.form.observacionesPlaceholder")}
              rows={2}
              {...register("observaciones")}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !hasSelection}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("facturacion.form.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// OrdenRow — fila de orden con checkbox
// =============================================================================

interface OrdenRowProps {
  orden: OrdenDisponible;
  checked: boolean;
  onToggle: (id: number) => void;
}

function OrdenRow({ orden, checked, onToggle }: OrdenRowProps) {
  const { t } = useTranslation();
  const total = parseFloat(orden.total);
  const isPagoDirecto = orden.tipo_cobro === "DIRECTO";

  return (
    <label
      className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors"
      htmlFor={`orden-${orden.id}`}
    >
      <Checkbox
        id={`orden-${orden.id}`}
        checked={checked}
        onCheckedChange={() => onToggle(orden.id)}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <ClipboardList className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-sm font-mono font-medium">{orden.numero}</span>
          <span className="text-xs text-muted-foreground">{orden.fecha}</span>
          {isPagoDirecto && (
            <Badge className="bg-green-600 hover:bg-green-700 text-xs">
              {t("facturacion.form.pagoDirectoBadge")}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {orden.examenes.map((e) => e.nombre).join(", ")}
        </p>
      </div>
      <span className="text-sm font-medium shrink-0">L. {total.toFixed(2)}</span>
    </label>
  );
}

// =============================================================================
// OrdenesAgrupadasPorPaciente — modo cliente
// =============================================================================

interface OrdenesAgrupadasProps {
  grupos: ReturnType<typeof agruparPorPaciente>;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
}

function OrdenesAgrupadasPorPaciente({ grupos, selectedIds, onToggle }: OrdenesAgrupadasProps) {
  return (
    <div className="rounded-md border divide-y max-h-[260px] overflow-y-auto">
      {grupos.map((grupo) => (
        <div key={grupo.paciente_nombre}>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/40 border-b">
            <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-semibold text-muted-foreground">
              {grupo.paciente_nombre || "—"}
            </span>
          </div>
          {grupo.ordenes.map((orden) => (
            <OrdenRow
              key={orden.id}
              orden={orden}
              checked={selectedIds.has(orden.id)}
              onToggle={onToggle}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
